import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Phone, 
  ShieldCheck, 
  Clock, 
  FileText, 
  CheckCircle2, 
  Scale, 
  MessageSquare,
  ChevronDown,
  MapPin,
  Users,
  DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';

const HERO_IMAGE = "https://images.pexels.com/photos/5668806/pexels-photo-5668806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SMSConsentPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    accidentType: '',
    preferredContactTime: '',
    message: ''
  });
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [nonMarketingConsent, setNonMarketingConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate form submission (no backend storage as per user request)
    setTimeout(() => {
      toast.success('Thank you! Your request has been submitted. We will contact you shortly.');
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        accidentType: '',
        preferredContactTime: '',
        message: ''
      });
      setMarketingConsent(false);
      setNonMarketingConsent(false);
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="sms-consent-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-[#1B3A5F] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B3A5F] to-[#0F172A]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-white space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm">
                <MapPin className="w-4 h-4 text-[#2563EB]" />
                <span>Serving South Carolina</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight" data-testid="hero-headline">
                Get Help With Your Auto Accident Claim
              </h1>
              
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
                Request a free case review and choose how you'd like us to stay in touch. 
                Text messaging helps us coordinate your case faster.
              </p>
              
              {/* Trust Points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm">No Upfront Cost</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm">60-Second Form</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm">SC Licensed Lawyers</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-sm">Fast Text Updates</span>
                </div>
              </div>

              <a 
                href="tel:+18034030488" 
                className="inline-flex items-center gap-2 text-[#60A5FA] hover:text-white transition-colors font-semibold text-lg"
                data-testid="hero-call-link"
              >
                <Phone className="w-5 h-5" />
                Or Call: +1 803-403-0488
              </a>
            </div>

            {/* Right Content - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8" data-testid="intake-form-card">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-[#1B3A5F] mb-2">Start Your Free Case Review</h2>
                <p className="text-slate-600 text-sm">Fill out the form below and we'll be in touch</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="intake-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-slate-700">First Name *</Label>
                    <Input 
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="John"
                      className="h-12"
                      required
                      data-testid="input-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-slate-700">Last Name *</Label>
                    <Input 
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      className="h-12"
                      required
                      data-testid="input-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input 
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="john@example.com"
                    className="h-12"
                    required
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Phone Number *</Label>
                  <Input 
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(803) 555-0123"
                    className="h-12"
                    required
                    data-testid="input-phone"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accidentType" className="text-slate-700">Accident Type</Label>
                  <Select onValueChange={(value) => handleSelectChange('accidentType', value)} value={formData.accidentType}>
                    <SelectTrigger className="h-12" data-testid="select-accident-type">
                      <SelectValue placeholder="Select accident type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rear-end">Rear-End Collision</SelectItem>
                      <SelectItem value="t-bone">T-Bone/Side Impact</SelectItem>
                      <SelectItem value="hit-and-run">Hit and Run</SelectItem>
                      <SelectItem value="truck-accident">Commercial/Truck Accident</SelectItem>
                      <SelectItem value="dui">DUI/Drunk Driver</SelectItem>
                      <SelectItem value="uninsured">Uninsured/Underinsured Driver</SelectItem>
                      <SelectItem value="multi-vehicle">Multi-Vehicle Accident</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="preferredContactTime" className="text-slate-700">Preferred Contact Time</Label>
                  <Select onValueChange={(value) => handleSelectChange('preferredContactTime', value)} value={formData.preferredContactTime}>
                    <SelectTrigger className="h-12" data-testid="select-contact-time">
                      <SelectValue placeholder="Select preferred time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="morning">Morning (8am - 12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm - 5pm)</SelectItem>
                      <SelectItem value="evening">Evening (5pm - 8pm)</SelectItem>
                      <SelectItem value="anytime">Anytime</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-slate-700">Brief Details (Optional)</Label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us briefly about your accident..."
                    className="min-h-[80px] resize-none"
                    data-testid="textarea-message"
                  />
                </div>

                {/* SMS Consent Checkboxes */}
                <div className="space-y-4 pt-2 border-t border-slate-200">
                  <p className="text-sm text-slate-600 font-medium pt-2">SMS Communication Preferences (Optional)</p>
                  
                  {/* Marketing Consent */}
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="marketingConsent"
                      checked={marketingConsent}
                      onCheckedChange={setMarketingConsent}
                      className="mt-1"
                      data-testid="checkbox-marketing-consent"
                    />
                    <Label htmlFor="marketingConsent" className="text-sm text-slate-600 leading-snug cursor-pointer">
                      I consent to receive marketing text messages, about special offers, discounts, and service updates, from Auto Accident Claim Assist at the phone number provided. Message frequency may vary. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                    </Label>
                  </div>

                  {/* Non-Marketing Consent */}
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id="nonMarketingConsent"
                      checked={nonMarketingConsent}
                      onCheckedChange={setNonMarketingConsent}
                      className="mt-1"
                      data-testid="checkbox-non-marketing-consent"
                    />
                    <Label htmlFor="nonMarketingConsent" className="text-sm text-slate-600 leading-snug cursor-pointer">
                      I consent to receive non-marketing text messages from Auto Accident Claim Assist about case follow-ups, intake updates, appointment scheduling, and claim-related notifications. Message frequency may vary, message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                    </Label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-lg rounded-lg shadow-lg transition-all transform hover:-translate-y-0.5"
                  data-testid="submit-button"
                >
                  {isSubmitting ? 'Submitting...' : 'Start Free Case Review'}
                </Button>

                {/* Legal Links Under Form */}
                <div className="text-center text-xs text-slate-500 pt-2">
                  By submitting, you agree to our{' '}
                  <Link to="/privacy-policy" className="text-[#1B3A5F] hover:underline" data-testid="form-privacy-link">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/terms-and-conditions" className="text-[#1B3A5F] hover:underline" data-testid="form-terms-link">Terms & Conditions</Link>.
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Why Text Messaging Section */}
      <section className="py-16 bg-slate-50" data-testid="why-text-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B3A5F] mb-4">
              Why We Use Text Messaging
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              We use text messaging to help coordinate your case review, follow up on your request, 
              send scheduling updates, and share occasional service or firm-related updates if you opt in.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow" data-testid="benefit-card-1">
              <div className="w-12 h-12 rounded-full bg-[#1B3A5F]/10 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-[#1B3A5F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1B3A5F] mb-2">Faster Communication</h3>
              <p className="text-slate-600">Get quick updates on your case status and respond on your schedule without waiting for calls.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow" data-testid="benefit-card-2">
              <div className="w-12 h-12 rounded-full bg-[#1B3A5F]/10 flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#1B3A5F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1B3A5F] mb-2">Appointment Reminders</h3>
              <p className="text-slate-600">Never miss an important appointment or deadline with convenient text reminders.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow" data-testid="benefit-card-3">
              <div className="w-12 h-12 rounded-full bg-[#1B3A5F]/10 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-[#1B3A5F]" />
              </div>
              <h3 className="text-xl font-semibold text-[#1B3A5F] mb-2">Your Control</h3>
              <p className="text-slate-600">You choose what messages to receive. Opt out anytime by texting STOP.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B3A5F] mb-4">How It Works</h2>
            <p className="text-lg text-slate-600">Simple steps to get help with your auto accident claim</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Submit Your Details',
                description: 'Fill out the simple form with your accident information and contact preferences.'
              },
              {
                step: '2',
                title: 'We Review Your Case',
                description: 'A licensed South Carolina attorney reviews the details you provided.'
              },
              {
                step: '3',
                title: 'Get a Response',
                description: 'Receive a call or text (if opted in) to discuss your situation and options.'
              },
              {
                step: '4',
                title: 'Move Forward',
                description: 'If you decide to proceed, the attorney guides you through next steps.'
              }
            ].map((item, index) => (
              <div key={index} className="relative text-center" data-testid={`step-${item.step}`}>
                <div className="w-16 h-16 rounded-full bg-[#1B3A5F] text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#1B3A5F] mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-slate-200" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1B3A5F] mb-4">Frequently Asked Questions</h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white rounded-lg border border-slate-200 px-6" data-testid="faq-item-1">
              <AccordionTrigger className="text-left text-[#1B3A5F] font-semibold hover:no-underline">
                Is there any cost to submit a case review?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                No, there is no cost to submit your information for a free case review. There's no obligation, 
                and you won't be charged anything to speak with an attorney about your situation.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white rounded-lg border border-slate-200 px-6" data-testid="faq-item-2">
              <AccordionTrigger className="text-left text-[#1B3A5F] font-semibold hover:no-underline">
                Why do you ask for SMS consent?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Text messaging allows us to send you quick updates about your case, appointment reminders, 
                and important notifications. It's completely optional—your form submission will be processed 
                regardless of whether you consent to text messages.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white rounded-lg border border-slate-200 px-6" data-testid="faq-item-3">
              <AccordionTrigger className="text-left text-[#1B3A5F] font-semibold hover:no-underline">
                How do I stop receiving text messages?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                You can stop text messages at any time by replying STOP to any message you receive from us. 
                You'll receive a confirmation that you've been unsubscribed. For help, reply HELP or contact 
                us at brendan@autoaccidentclaimassist.com or +1 803-403-0488.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white rounded-lg border border-slate-200 px-6" data-testid="faq-item-4">
              <AccordionTrigger className="text-left text-[#1B3A5F] font-semibold hover:no-underline">
                What if I'm not sure who was at fault?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Fault can be complicated in auto accidents. That's why we offer a free case review—an attorney 
                can review your situation and help explain possible paths forward, regardless of fault uncertainty.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white rounded-lg border border-slate-200 px-6" data-testid="faq-item-5">
              <AccordionTrigger className="text-left text-[#1B3A5F] font-semibold hover:no-underline">
                What if my injuries appeared later?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                Many auto accident injuries show up days or even weeks after the incident. A case review 
                can still be helpful to understand your options, even if some time has passed since the accident.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B3A5F] mb-4">
            Ready to Get Help With Your Claim?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Start your free case review today. No obligation, no upfront cost.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="h-14 px-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-lg rounded-lg shadow-lg transition-all transform hover:-translate-y-0.5"
              data-testid="final-cta-review-button"
            >
              Start Free Case Review
            </Button>
            <a 
              href="tel:+18034030488"
              className="inline-flex items-center justify-center h-14 px-8 bg-transparent border-2 border-[#1B3A5F] text-[#1B3A5F] hover:bg-[#1B3A5F] hover:text-white font-semibold text-lg rounded-lg transition-all"
              data-testid="final-cta-call-button"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call +1 803-403-0488
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SMSConsentPage;
